import type { ReploMixedStyleValue } from "replo-runtime/store/utils/mixed-values";
import type { BorderWidthModifierValue } from "schemas/generated/fileModifiers";

import React, { useCallback, useEffect, useMemo, useState } from "react";

import DirectionBottom from "@svg/modifiers/direction/direction-bottom";
import DirectionLeft from "@svg/modifiers/direction/direction-left";
import DirectionRight from "@svg/modifiers/direction/direction-right";
import DirectionTop from "@svg/modifiers/direction/direction-top";
import DirectionUnified from "@svg/modifiers/direction/direction-unified";
import ExpandModifierOptions from "@svg/modifiers/expand-modifier-options";

import { useSemanticModifierValue } from "@agent-ui/features/modifiers/hooks/useSemanticModifierValue";
import { parsePixelValue } from "@agent-ui/features/modifiers/utils/parseComputedValue";
import { Button } from "@replo/design-system/components/button/Button";
import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import { LengthInputSelector } from "@v2/features/modifiers/components/LengthInputSelector";
import { ChevronDown } from "lucide-react";
import { useOverridableState } from "replo-runtime/shared/hooks/useOverridableState";
import { parseUnit } from "replo-runtime/shared/utils/units";
import { REPLO_MIXED_STYLE_VALUE } from "replo-runtime/store/utils/mixed-values";

type BorderMode = "unified" | "individual";

const BORDER_WIDTH_OPTIONS = [
  { label: "None", value: "0" },
  { label: "1px", value: "1" },
  { label: "2px", value: "2" },
  { label: "4px", value: "4" },
  { label: "6px", value: "6" },
  { label: "8px", value: "8" },
];

interface BorderValue {
  top: number | null;
  right: number | null;
  bottom: number | null;
  left: number | null;
}

interface BorderWidthModifierProps {
  onChange: (modifiers: BorderWidthModifierValue[]) => void;
}

const COMMON_LENGTH_INPUT_PROPS = {
  placeholder: "0px",
  metrics: ["px"],
  unitDefaults: { value: "0", unit: "px" },
  menuOptions: BORDER_WIDTH_OPTIONS,
  minValues: { px: 0 },
  inputClassName: "w-full",
  endEnhancer: () => <ChevronDown size={12} />,
};

const getInitialWidthMode = (borderValue: BorderValue | null): BorderMode => {
  if (!borderValue) {
    return "unified";
  }
  const { top, right, bottom, left } = borderValue;
  if (top === right && right === bottom && bottom === left) {
    return "unified";
  }
  return "individual";
};

interface BorderWidthInputProps {
  direction: "top" | "right" | "bottom" | "left";
  value: number | null | undefined;
  onChange: (value: number | undefined) => void;
  icon?: React.ReactNode;
  displayMixed?: boolean;
  tooltipText: string;
}

const BorderWidthInput: React.FC<BorderWidthInputProps> = ({
  tooltipText,
  value,
  onChange,
  icon,
  displayMixed,
}) => {
  let valueProp: string | ReploMixedStyleValue | null;
  if (displayMixed) {
    valueProp = REPLO_MIXED_STYLE_VALUE as ReploMixedStyleValue;
  } else if (value != null) {
    valueProp = `${value}px`;
  } else {
    valueProp = null;
  }

  return (
    <Tooltip
      triggerAsChild={false}
      side="top"
      align="start"
      content={tooltipText}
    >
      <LengthInputSelector
        field="borderWidth"
        value={valueProp}
        onChange={(newValue) => {
          const result = parseUnit(
            newValue,
            { value: 0, unit: "px" },
            "default",
            "px",
          );
          const numericValue =
            typeof result.value === "number"
              ? result.value
              : Number.parseFloat(result.value);
          onChange(numericValue);
        }}
        startEnhancer={
          <Tooltip
            triggerAsChild={false}
            side="top"
            align="start"
            content={tooltipText}
          >
            {icon}
          </Tooltip>
        }
        {...COMMON_LENGTH_INPUT_PROPS}
      />
    </Tooltip>
  );
};

const BorderWidthModifier: React.FC<BorderWidthModifierProps> = ({
  onChange,
}) => {
  const topWidth = useSemanticModifierValue({
    semanticExtractor: ({ computedStyles }) => {
      return parsePixelValue(computedStyles.borderTopWidth) ?? null;
    },
  });
  const rightWidth = useSemanticModifierValue({
    semanticExtractor: ({ computedStyles }) => {
      return parsePixelValue(computedStyles.borderRightWidth) ?? null;
    },
  });
  const bottomWidth = useSemanticModifierValue({
    semanticExtractor: ({ computedStyles }) => {
      return parsePixelValue(computedStyles.borderBottomWidth) ?? null;
    },
  });
  const leftWidth = useSemanticModifierValue({
    semanticExtractor: ({ computedStyles }) => {
      return parsePixelValue(computedStyles.borderLeftWidth) ?? null;
    },
  });

  const extractedBorderWidths = useMemo(
    () => ({
      top: topWidth ?? null,
      right: rightWidth ?? null,
      bottom: bottomWidth ?? null,
      left: leftWidth ?? null,
    }),
    [topWidth, rightWidth, bottomWidth, leftWidth],
  );

  const [currentWidths, setCurrentWidths] = useOverridableState<BorderValue>(
    extractedBorderWidths ?? {
      top: null,
      right: null,
      bottom: null,
      left: null,
    },
  );

  const [widthMode, setWidthMode] = useState<BorderMode>(() => {
    return getInitialWidthMode(currentWidths);
  });

  useEffect(() => {
    setWidthMode(getInitialWidthMode(extractedBorderWidths));
  }, [extractedBorderWidths]);

  const updateBorderWidth = useCallback(
    (updates: Partial<BorderValue>) => {
      const newValue: BorderValue = { ...currentWidths, ...updates };
      setCurrentWidths(newValue);

      const modifiers: BorderWidthModifierValue[] = [];

      if (updates.top !== undefined) {
        modifiers.push({
          type: "borderWidth",
          direction: "top",
          value: updates.top as number,
        });
      }
      if (updates.right !== undefined) {
        modifiers.push({
          type: "borderWidth",
          direction: "right",
          value: updates.right as number,
        });
      }
      if (updates.bottom !== undefined) {
        modifiers.push({
          type: "borderWidth",
          direction: "bottom",
          value: updates.bottom as number,
        });
      }
      if (updates.left !== undefined) {
        modifiers.push({
          type: "borderWidth",
          direction: "left",
          value: updates.left as number,
        });
      }

      onChange(modifiers);
    },
    [currentWidths, setCurrentWidths, onChange],
  );

  const widthInputConfigs: Record<
    BorderMode,
    {
      key: string;
      label: string;
      value: number | null | undefined;
      onChange: (newValue: number | undefined) => void;
      icon?: React.ReactNode;
      displayMixed?: boolean;
    }[]
  > = useMemo(() => {
    const { top, right, bottom, left } = currentWidths ?? {};
    const isUnifiedMixed =
      [top, right, bottom, left].some((v) => v != null) &&
      !(top === right && right === bottom && bottom === left);

    return {
      unified: [
        {
          key: "unified",
          label: "Border Width",
          icon: <DirectionUnified className="w-4 h-4" />,
          value: top,
          displayMixed: Boolean(isUnifiedMixed),
          onChange: (newValue) => {
            updateBorderWidth({
              top: newValue,
              right: newValue,
              bottom: newValue,
              left: newValue,
            });
          },
        },
      ],
      individual: [
        {
          key: "top",
          label: "Border Top Width",
          icon: <DirectionTop className="w-4 h-4" />,
          value: top,
          onChange: (newValue) => {
            updateBorderWidth({ top: newValue });
          },
        },
        {
          key: "right",
          label: "Border Right Width",
          icon: <DirectionRight className="w-4 h-4" />,
          value: right,
          onChange: (newValue) => {
            updateBorderWidth({ right: newValue });
          },
        },
        {
          key: "bottom",
          label: "Border Bottom Width",
          icon: <DirectionBottom className="w-4 h-4" />,
          value: bottom,
          onChange: (newValue) => {
            updateBorderWidth({ bottom: newValue });
          },
        },
        {
          key: "left",
          label: "Border Left Width",
          icon: <DirectionLeft className="w-4 h-4" />,
          value: left,
          onChange: (newValue) => {
            updateBorderWidth({ left: newValue });
          },
        },
      ],
    };
  }, [currentWidths, updateBorderWidth]);

  const widthInputs = widthInputConfigs[widthMode];

  const handleWidthModeToggle = () => {
    setWidthMode(widthMode === "unified" ? "individual" : "unified");
  };

  return (
    <div className="border-width-modifier">
      {widthMode === "unified" ? (
        <div className="grid grid-cols-[1fr_min-content] gap-2 items-center">
          {widthInputs.map((input) => (
            <BorderWidthInput
              key={input.key}
              direction={input.key as any}
              value={input.value}
              tooltipText={input.label}
              onChange={(v) => {
                input.onChange(v);
              }}
              icon={input.icon}
              displayMixed={input.displayMixed}
            />
          ))}
          <Tooltip content="Set individual borders">
            <Button
              icon
              variant="secondary"
              size="sm"
              onClick={handleWidthModeToggle}
            >
              <ExpandModifierOptions />
            </Button>
          </Tooltip>
        </div>
      ) : (
        <div className="grid grid-cols-[1fr_1fr_min-content] gap-2 items-center">
          {widthInputs.slice(0, 2).map((input) => (
            <BorderWidthInput
              key={input.key}
              tooltipText={input.label}
              direction={input.key as any}
              value={input.value}
              onChange={input.onChange}
              icon={input.icon}
              displayMixed={input.displayMixed}
            />
          ))}
          <Tooltip content="Use unified width">
            <Button
              icon
              variant="secondary"
              size="sm"
              onClick={handleWidthModeToggle}
            >
              <ExpandModifierOptions />
            </Button>
          </Tooltip>
          {widthInputs.slice(2).map((input) => (
            <BorderWidthInput
              key={input.key}
              tooltipText={input.label}
              direction={input.key as any}
              value={input.value}
              onChange={input.onChange}
              icon={input.icon}
              displayMixed={input.displayMixed}
            />
          ))}
          <div></div>
        </div>
      )}
    </div>
  );
};

export default BorderWidthModifier;
